import React, { useState, useRef } from 'react'

import { CertificateAdmin, CertificateReplacementDelivery } from 'types'

import { Box, Stack, Typography, alpha, useTheme } from '@mui/material'
import { SendIcon, WrenchIcon } from 'lucide-react'

import { Button } from '@repo/ui'
import { Modal, Alert, TextArea } from '@repo/ui'
import { ScanSessionBlock, ScanSessionBlockRef } from 'components'
import { ConfirmSettingModal } from '../ConfirmSettingModal'

/**
 * The delivery question's two answers, as SELECTABLE cards (working-conventions
 * §34) — deliberately NOT `MethodOption`'s action-row treatment (border-on-
 * hover), because that pattern is for a click that navigates away. This choice
 * has a persistent state — one answer stays picked until the operator changes
 * it or the modal closes — so hover is a brand TINT only ("considering") and
 * the brand BORDER is reserved for whichever one is actually selected
 * ("chosen"). Mirrors `FeaturesPage`'s activation-trigger tiles, which is the
 * canonical instance of this pattern.
 */
const DeliveryOption = ({
  icon,
  title,
  description,
  selected,
  onClick,
  disabled,
}: {
  icon: React.ReactNode
  title: string
  description: string
  selected: boolean
  onClick: () => void
  disabled?: boolean
}) => {
  const theme = useTheme()
  return (
    <Box
      role="radio"
      aria-checked={selected}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onClick={disabled ? undefined : onClick}
      onKeyDown={e => {
        if (disabled) return
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 3,
        p: 3,
        flex: 1,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        borderRadius: '8px',
        border: `1px solid ${selected ? theme.palette.brand[500] : theme.palette.primary[100]}`,
        backgroundColor: selected
          ? alpha(theme.palette.brand[500], 0.1)
          : theme.palette.background.paper,
        transition: 'background-color 120ms ease, border-color 120ms ease',
        ...(!disabled && {
          '&:hover': {
            backgroundColor: selected
              ? alpha(theme.palette.brand[500], 0.1)
              : alpha(theme.palette.brand[500], 0.06),
          },
        }),
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          width: 32,
          height: 32,
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.primary[50],
        }}
      >
        {icon}
      </Box>
      <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body3" sx={{ fontWeight: 500 }}>
          {title}
        </Typography>
        <Typography variant="body4" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          {description}
        </Typography>
      </Stack>
    </Box>
  )
}

/**
 * VRG-268 — move a certificate onto a replacement physical tag.
 *
 * This is a CONFIRM-BEFORE-APPLY surface (§43): nothing is mutated until the operator presses
 * the confirm button, and the consequence is stated in the body rather than being discovered
 * afterwards. It is not built on `ConfirmSettingModal` because that component confirms a single
 * staged VALUE — it has no room for the capture step (a live scan session) or the reason field,
 * both of which have to happen before there is a value to confirm. It matches
 * ConfirmSettingModal's treatment instead: the consequence renders as a warning Alert, and the
 * confirm label is the verb, never "OK".
 *
 * THE REPLACEMENT IS SCANNED, NEVER TYPED (user decision, 2026-08-11). There used to be an
 * "or enter the tag UID" field beside the scan session, justified as a fallback for a desk
 * reader. It is removed and must not come back. A typed uid is an ASSERTION that a chip is in
 * the operator's hand; a scan is EVIDENCE of it. This flow permanently kills the tag a customer
 * is holding, so accepting the assertion means a typo — or a uid copied off a spreadsheet —
 * can retire a live tag and bind the certificate to a chip nobody has. The scan cannot express
 * that mistake, because the tag has to physically exist and be present to produce the uid.
 */
export const ReplaceTagModal = ({
  open,
  onClose,
  refetch,
  certificate,
}: {
  open: boolean
  refetch: () => void
  onClose: () => void
  certificate: CertificateAdmin | null
}) => {
  const [pendingCount, setPendingCount] = useState<number>(0)
  const [isReplacingTag, setIsReplacingTag] = useState<boolean>(false)
  const [reason, setReason] = useState<string>('')
  const [isConfirming, setIsConfirming] = useState<boolean>(false)
  // VRG-275 — the workflow question. 'fitted' (bench repair) is the default and today's
  // exact prior behaviour; 'posted' is new and holds the replacement until the owner
  // confirms receipt. See the DeliveryOption cards below and the consequence copy, both
  // of which change with this choice.
  const [delivery, setDelivery] = useState<CertificateReplacementDelivery>('fitted')
  const scanSessionRef = useRef<ScanSessionBlockRef>(null)

  // A replacement arrives exactly ONE way: tapped into the scan session — see the header note.
  const canReplace = pendingCount === 1

  /**
   * VRG-388 — ONE surface, label follows record state (§46).
   *
   * A tagless certificate is not an error state to be gated out of this modal; it is the ADD
   * case. Demo certificates are born tagless (provisioning inserts no tag), so this is how a
   * link-only demo becomes a physical Tier-1 send.
   *
   * Read from the RAW columns and not from "does it resolve to a tag row", matching the
   * server's own guard: a `tag_uid` naming no `tags` row is still an identifier, and treating
   * it as absent here would offer Add on a certificate the server will refuse to add to.
   * VRG-231: the `e_tag_id` term that used to be in this chain is gone — it was an alias of
   * `tag_uid` carrying the identical value, so dropping it changes nothing about the outcome.
   *
   * Everything downstream branches off this ONE derivation rather than re-deriving, so the
   * title, the button, the consequence, the endpoint and the confirm can never disagree about
   * which operation is happening.
   */
  const hasTag = Boolean(certificate?.tag_uid ?? certificate?.tag_id)
  const isAdding = Boolean(certificate) && !hasTag
  const actionLabel = isAdding ? 'Add tag' : 'Replace tag'

  // VRG-388 — a demo/intro certificate. Tagging one is the deliberate act of putting scraped,
  // guessed content behind a chip that genuinely verifies, so the consequence copy names it.
  // Tagging does NOT clear the flag: the certificate stays off the billing meter and keeps its
  // Demo badge, which is the property that makes attaching a tag acceptable at all.
  const isExample = certificate?.example_kind != null

  // Whether someone already owns this item. Replacing the tag on unsold stock and on a
  // customer's handbag are different acts, and the consequence line has to say so: in the
  // second case the tag that stops working is one a person is physically holding.
  const hasOwner = Boolean(certificate?.customer_id || certificate?.customer)

  // STATES the consequence; does not instruct. The owned line used to end "Make sure the
  // replacement tag is on the item before you continue," which assumes the operator is
  // holding the item. A brand that posts the replacement to the owner cannot follow that,
  // and the sentence quietly implied the old tag keeps working until the new one is fitted.
  // It does not: the old tag dies the moment this is confirmed, which is the security
  // property that makes replace safe. So say that, and name the gap it creates rather than
  // give advice that only fits one of the two workflows.
  //
  // VRG-275 — the "if you're sending the new tag to the owner" hedge is gone: that used to
  // be a guess about a workflow this modal couldn't see. Now that it's an explicit choice,
  // each branch states what actually happens under it instead of hedging across both.
  //
  // VRG-388 — the ADD consequence is a different KIND of statement. Replace's consequence is a
  // loss (a live tag dies); add's is a gain, and the thing worth warning about is what the
  // certificate BECOMES: until now it existed only as a link, and afterwards there is a
  // physical object in the world that verifies against it. For a demo that is the whole point
  // and also the whole risk, so the demo case says so explicitly rather than leaving the
  // operator to infer it.
  const consequence = isAdding
    ? isExample
      ? 'This is demo data. Attaching a tag turns it into a physical item that scans and verifies like a real one, while the certificate itself stays marked as a demo. Only do this for a demo you are sending to a prospect.'
      : 'This certificate can currently be reached by link only. After you confirm, scanning this tag will reach it, and the tag cannot be swapped except through Replace tag.'
    : delivery === 'posted'
    ? hasOwner
      ? "The owner's current tag stops working the moment you confirm. The replacement will not work until they confirm they've received it. Until then, this item can't be scanned by anyone. That gap is deliberate: a tag that is live in the post is one an interceptor could use."
      : "The current tag stops working the moment you confirm. The replacement will not work until the owner confirms they've received it. Until then, this item can't be scanned by anyone. That gap is deliberate: a tag that is live in the post is one an interceptor could use."
    : hasOwner
      ? "The owner's current tag stops working the moment you confirm. Until the replacement is on the item, scanning it won't reach this certificate."
      : 'The current tag stops working the moment you confirm. Anyone who scans it will no longer reach this certificate.'

  const handleReplaceCertificateTag = async () => {
    if (!certificate?.id || !canReplace) {
      return
    }

    setIsReplacingTag(true)
    try {
      // VRG-388 — two named operations, never one call with a mode flag. The server refuses
      // each other's inputs, so the worst a wrong branch here can do is produce a refusal.
      if (isAdding) {
        await scanSessionRef.current?.addCertificateTag(certificate.id)
      } else {
        await scanSessionRef.current?.replaceCertificateTag(certificate.id, { reason, delivery })
      }
      refetch()
      setIsConfirming(false)
      handleClose()
    } catch (error) {
      // Error handling is done in ScanSessionBlock. Drop back to the capture modal rather
      // than closing — the scan and the reason are still there, so a retry costs nothing.
      setIsReplacingTag(false)
      setIsConfirming(false)
      return
    }
  }

  const handleClose = () => {
    if (isReplacingTag) {
      return // Prevent closing while replacing
    }
    setPendingCount(0)
    setReason('')
    setDelivery('fitted')
    setIsReplacingTag(false)
    setIsConfirming(false)
    onClose()
  }

  /**
   * Live STATUS of the scan, not instructions.
   *
   * "Scan exactly one tag to continue" used to live here, which made this line do two jobs:
   * tell you the rule before you act, and report where you got to. The rule moved up beside
   * the inventory constraint (§40), so nothing is reported until there is something to
   * report — at zero scans the instruction above is the whole message, and repeating it
   * down here would just be the same sentence twice.
   */
  const getStatusText = () => {
    if (pendingCount === 0) {
      return null
    }
    if (pendingCount === 1) {
      return isAdding ? 'Ready to add tag' : 'Ready to replace tag'
    }
    return `${pendingCount} tags scanned. Only one tag is allowed for ${isAdding ? 'adding' : 'replacement'}`
  }

  const statusText = getStatusText()

  return (
    <>
    <Modal
      title={actionLabel}
      open={open}
      onClose={handleClose}
      actions={
        <>
          <Button
            disableElevation
            variant="secondary"
            onClick={handleClose}
            disabled={isReplacingTag}
          >
            Cancel
          </Button>
          <Button
            disableElevation
            onClick={() => setIsConfirming(true)}
            disabled={!canReplace || isReplacingTag}
            loading={isReplacingTag}
          >
            {isReplacingTag
              ? isAdding
                ? 'Adding tag...'
                : 'Replacing tag...'
              : actionLabel}
          </Button>
        </>
      }
    >
      <Stack spacing={4}>
        {certificate && (
          // The product, not the certificate's uuid. The previous version printed the raw
          // certificate id at the operator, which is unreadable, unverifiable at a glance, and
          // tells them nothing about whether they have the right item in their hands.
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            {isAdding ? 'Adding a tag to ' : 'Replacing the tag on '}
            <strong>{certificate.product?.title || 'this item'}</strong>
            {isAdding
              ? // Says what the certificate IS right now, which is the fact that makes Add the
                // right operation.
                '. It has no tag yet, so it can only be reached by link.'
              : // VRG-231: this used to print the current chip UID, to answer "am I on the
                // right record". The product title in the same sentence already answers it,
                // and the UID is no longer surfaced to a human anywhere in the product.
                '.'}
          </Typography>
        )}

        {/*
          VRG-275 — the ONE question this modal cannot infer on its own: does the
          replacement get fitted right now, or posted to the owner? Asked BEFORE the
          consequence line below, because the consequence genuinely differs by answer and
          has to reflect the choice, not the other way around.
        */}
        {/* VRG-388 — HIDDEN when adding, not disabled. The question is "does the replacement
            get fitted now, or posted while the old tag is already dead?", and in the add flow
            neither half exists: there is no old tag to retire, so there is no unscannable
            window to protect against, and a demo has no owner to post to. Showing it greyed
            out would imply the choice matters here. The API is given no `delivery` at all. */}
        {!isAdding && (
        <Stack spacing={2}>
          <Typography variant="body2">How is the replacement reaching the item?</Typography>
          <Stack direction="row" spacing={3} role="radiogroup" aria-label="Replacement delivery">
            <DeliveryOption
              icon={<WrenchIcon strokeWidth={1} size={18} />}
              title="I'm fitting it now"
              description="The item is here. The replacement goes live as soon as you confirm."
              selected={delivery === 'fitted'}
              onClick={() => setDelivery('fitted')}
              disabled={isReplacingTag}
            />
            <DeliveryOption
              icon={<SendIcon strokeWidth={1} size={18} />}
              title="I'm sending it to the owner"
              description="The replacement is held until the owner confirms they've received it."
              selected={delivery === 'posted'}
              onClick={() => setDelivery('posted')}
              disabled={isReplacingTag}
            />
          </Stack>
        </Stack>
        )}

        <Alert severity="warning" description={consequence} />

        {/*
          BOTH constraints, together, BEFORE the scan — not discovered as a refusal after it.

          The first is the server's: the replacement must be a tag assigned to this company AND
          already received into inventory (the VRG-250 ownership guard), which is invisible from
          here, so an operator reaching for a blank tag or one still in transit would otherwise
          just get told "not your tag".

          The second used to sit at the BOTTOM of the modal, below the Reason field, where it
          restated a rule the operator could only break by having already scanned wrong. Guidance
          belongs before the decision it governs (§40), and the two sentences are one instruction:
          which tag, and how many.
        */}
        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          {isAdding ? 'The tag' : 'The replacement'} must be a tag from your own inventory that
          you have already received. Scan exactly one tag to continue.
        </Typography>

        <ScanSessionBlock
          ref={scanSessionRef}
          // Presentational: swaps the scan prompt, and turns "already in use with another
          // certificate" into "Tag already certified, try another" — a wrong-tag-in-hand
          // moment in both flows, not a dead end.
          intent={isAdding ? 'add' : 'replace'}
          onPendingCountChange={setPendingCount}
          singleTag={true}
        />

        {/* VRG-388 — no Reason when adding. The field's own helper says it records "why the
            tag changed", and nothing changed: the certificate had no tag and now has one.
            There is no prior state to explain, the API accepts no reason on this path, and a
            required-looking box with nothing true to put in it invites invented history in an
            audit table. The add is still audited — as `assign_tag`, with the operator, the
            uid and the session. */}
        {!isAdding && (
          <TextArea
            label="Reason"
            placeholder="Damaged, lost, failed in the field"
            value={reason}
            onValueChange={setReason}
            disabled={isReplacingTag}
            minRows={2}
            maxRows={4}
            helperText="Recorded on the certificate's history. This is the only record of why the tag changed."
          />
        )}

        {statusText && (
          <Typography
            variant="body2"
            color={canReplace ? 'success.main' : 'text.secondary'}
            sx={{ fontWeight: canReplace ? 600 : 400, lineHeight: 1.5 }}
          >
            {statusText}
          </Typography>
        )}
      </Stack>
    </Modal>

    {/*
      THE SECOND CONFIRM, and why it is not "Are you sure?".

      The instinct after building this flow was that a permanent, billed, customer-facing
      act should not fire on one click. True, but a generic second dialog answers the wrong
      objection. The consequence is ALREADY stated above in a warning Alert, so repeating it
      would be the same sentence twice, and clicking "yes" after clicking "replace" is the
      same reflex twice — that trains click-through rather than attention.

      Note also that a second confirm is not the flow's first safeguard. Reaching an enabled
      "Replace tag" requires physically tapping the replacement chip, an act that cannot
      happen by mis-click. What that scan CANNOT catch is the one mistake left: the right tag
      in your hand and the WRONG CERTIFICATE open, because the kebab was opened on the wrong
      row. No amount of "are you sure?" touches that.

      So this step's whole job is to name the specific thing about to be destroyed — this
      product, this uid — turning the second click from "yes" into "yes, that one". Which is
      also why it reuses ConfirmSettingModal (§28) rather than hand-rolling: state the
      consequence, then ask, with the verb as the label.

      NOT SHOWN, and worth adding: the uid of the tag being scanned IN. "Old X becomes new Y"
      is stronger than naming the old alone, but `ScanSessionBlock` only reports a pending
      COUNT upward, not the uid, so it needs a change there.
    */}
    <ConfirmSettingModal
      open={isConfirming}
      onClose={() => setIsConfirming(false)}
      onConfirm={handleReplaceCertificateTag}
      title={
        isAdding
          ? `Add a tag to ${certificate?.product?.title || 'this item'}?`
          : `Replace the tag on ${certificate?.product?.title || 'this item'}?`
      }
      consequence={
        isAdding ? (
          // Names the specific thing being created, for the same reason the replace branch
          // names the specific thing being destroyed: the mistake this step exists to catch is
          // the right tag in hand and the WRONG CERTIFICATE open.
          <>
            This creates a physical item that scans and verifies as{' '}
            <strong>{certificate?.product?.title || 'this item'}</strong>.
            {isExample
              ? ' It is demo data, so what it verifies is a scraped, not-real record. It stays marked as a demo and is never billed.'
              : ''}{' '}
            The tag cannot be swapped afterwards except through Replace tag.
          </>
        ) : (
        <>
          {/* VRG-231: this used to name the chip UID ("Tag 04A2... stops working"). The UID is
              no longer displayed anywhere, so this is now unconditionally the generic phrasing
              that was already the fallback branch. */}
          The current tag stops working the moment you confirm
          {' '}and will no longer reach this certificate.
          {/* Identity, not instruction. "Make sure the replacement is on the item" is already
              said in the modal behind this one; repeating it here is the padding that makes a
              confirm skimmable. What this adds is WHOSE tag it is, which changes the stakes. */}
          {hasOwner ? ' It is the tag on the owner’s item.' : ''}
          {/* VRG-275 — the one fact this confirm must not let the operator assume: for a
              posted delivery, this click does NOT make the replacement live. Without this
              line the confirm reads identically to the fitted case, and "Replace tag" easily
              implies the swap is complete on click. */}
          {delivery === 'posted'
            ? ' The replacement will stay held, not live, until the owner confirms they’ve received it.'
            : ''}
        </>
        )
      }
      confirmLabel={actionLabel}
      loading={isReplacingTag}
    />
    </>
  )
}
